import React, { useEffect, useRef, useState, useContext } from 'react';
import trackEvent from '@/client/utils/analytics/trackEvent';
import { linkGeneratorHelper, generateLinkObject } from '@/utils/links/linkGeneratorHelper';
import getClientPageQueryObject from '@/client/utils/getClientPageQueryObject';
import experiments from '@/utils/labs/experiments';
import EnvironmentContext from '@/client/contexts/EnvironmentContext';
import pushToRouter from '@/client/utils/pushToRouter';
import removeSavedSearchFlag from '@/client/utils/searchRequestUtils';
import { Pill, Pills, TextRegular, SuccessBadge } from '@hz-design-system/web-ui';
import getIsMobile from '@/client/utils/browser/isMobile';
import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import HighlightGuide from '@/client/components/HighlightGuide/HighlightGuide';
import Classes from './AttributeSuggestionTooltip.module.scss';
import filterChipsHelper from '@/client/utils/filterChipsHelper';

type TAttributeSuggestionTooltipProps = {
  suggestedAttributes?: NonNullable<TSuggestedCategories['attributes']>['entities'];
  searchRequestObject: TSearchRequestObject;
  isSticky: boolean;
  isCarCategory: boolean;
  categoriesById: TCategories;
  facets: TFacet[];
};

const AttributeSuggestionTooltip: React.FC<TAttributeSuggestionTooltipProps> = ({
  suggestedAttributes,
  searchRequestObject,
  isSticky,
  isCarCategory,
  categoriesById,
  facets,
}) => {
  const { t } = useI18nContext();
  const chips = filterChipsHelper(searchRequestObject, categoriesById, facets, t);
  const { labsConfig } = useContext(EnvironmentContext);
  const { isAttributeSuggestionTooltipEnabled, isOnlySuggestionTooltipEnabled } = experiments({
    labsConfig,
  });
  const [showGuide, setShowGuide] = useState(false);
  const [triggerGuideUpdate, setTriggerGuideUpdate] = useState(0);
  const targetRef = useRef<HTMLDivElement>(null);
  const LRP_SUGGESTION_HIGHLIGHT_GUIDE_SEEN_KEY = 'LrpSuggestionHighlightGuideSeen';
  useEffect(() => {
    if (!suggestedAttributes?.length) return;
    trackEvent({
      eventAction: 'AttributeTooltipDisplay',
      eventMetric: { eventCategory: 'LRP' },
    });
  }, [suggestedAttributes]);
  useEffect(() => {
    const hasSeenGuide = localStorage.getItem(LRP_SUGGESTION_HIGHLIGHT_GUIDE_SEEN_KEY) ?? '0';
    if (hasSeenGuide === '0' && suggestedAttributes?.length) {
      setShowGuide(true);
    }
  }, [suggestedAttributes?.length]);

  const handleCloseGuide = () => {
    localStorage.setItem(LRP_SUGGESTION_HIGHLIGHT_GUIDE_SEEN_KEY, '1');
    setShowGuide(false);
  };
  useEffect(() => {
    if (!suggestedAttributes?.length || !isSticky || !isAttributeSuggestionTooltipEnabled) return;

    const header = document.querySelector('.hz-Header');
    if (!header) return;

    const filterChipsElement = document.querySelector('.pillsContainer');

    let pendingAnimationFrameId: number | null = null;

    const observer = new ResizeObserver(() => {
      if (pendingAnimationFrameId !== null) {
        cancelAnimationFrame(pendingAnimationFrameId);
      }
      pendingAnimationFrameId = window.requestAnimationFrame(() => {
        const height = header.getBoundingClientRect().height;
        if (filterChipsElement) {
          const filterChipsHeight = filterChipsElement.getBoundingClientRect().height;
          const isMobile = getIsMobile();
          if (suggestedAttributes?.length && filterChipsHeight > 0) {
            document.documentElement.style.setProperty('--filter-margin-bottom', isMobile ? '0px' : '8px');
          } else {
            document.documentElement.style.removeProperty('--filter-margin-bottom');
          }
          const extraHeight = filterChipsHeight > 0 ? 4 : 0;
          const mobileHeight = isMobile ? 4 : 0;
          document.documentElement.style.setProperty(
            '--attribute-suggested-height',
            `${height + filterChipsHeight + extraHeight + mobileHeight}px`,
          );
          if (showGuide) {
            setTriggerGuideUpdate((prev) => prev + 1);
          }
        }
        pendingAnimationFrameId = null;
      });
    });

    observer.observe(header);
    if (filterChipsElement) {
      observer.observe(filterChipsElement);
    }

    return () => {
      observer.disconnect();
      if (pendingAnimationFrameId !== null) {
        cancelAnimationFrame(pendingAnimationFrameId);
      }
    };
  }, [suggestedAttributes?.length, isSticky, showGuide, isAttributeSuggestionTooltipEnabled]);
  if (!suggestedAttributes?.length) {
    return null;
  }

  if (isCarCategory && isOnlySuggestionTooltipEnabled) {
    return null;
  }
  const redirectURL = (attribute: NonNullable<TSuggestedCategories['attributes']>['entities'][number]) => {
    trackEvent({
      eventAction: 'AttributeTooltipClick',
      eventLabel: attribute.attributeValueLabel || '',
      eventMetric: { eventCategory: 'LRP' },
    });
    if (showGuide) {
      handleCloseGuide();
    }
    const customAttr = {
      attributeValueId: attribute.attributeValueId,
      attributeValueLabel: attribute.attributeValueLabel,
      attributeValueKey: attribute.attributeValueKey,
      attributeLabel: attribute.attributeGroupLabel,
    };
    const attributegroup = attribute.attributeGroupKey || '';
    const attributes = attributegroup
      ? {
          ...(searchRequestObject.attributes ?? {}),
          [attributegroup]: [...(searchRequestObject.attributes?.[attributegroup] ?? []), customAttr],
        }
      : searchRequestObject.attributes;
    const options = {
      query: searchRequestObject.searchQuery?.trim() || '',
      attributes: attributes,
      searchRequestObject: removeSavedSearchFlag(searchRequestObject),
      withAllAttributes: false,
      removeAllFilters: false,
      removeAllAttributes: false,
    };
    const href = linkGeneratorHelper(options);
    const query = getClientPageQueryObject(generateLinkObject(options));

    pushToRouter({ query, href });
  };
  const getDisplayLabel = (attribute: NonNullable<TSuggestedCategories['attributes']>['entities'][number]) => {
    const valueLabel = attribute.attributeValueLabel || '';

    const isDuplicate =
      suggestedAttributes.filter((attr) => attr.attributeValueLabel === valueLabel).length > 1 ||
      chips?.some((chip) => chip.label === valueLabel);
    if (isDuplicate) {
      return `${attribute.attributeGroupLabel}-${valueLabel}`;
    }
    return valueLabel;
  };
  return (
    isAttributeSuggestionTooltipEnabled && (
      <>
        <div
          ref={targetRef}
          suppressHydrationWarning={true}
          className={isSticky ? Classes.stickyBg : Classes.plainBg}
          id="attribute-suggestion-tooltip"
        >
          <Pills>
            <TextRegular className={Classes.textRegular}>
              <SuccessBadge className={Classes.successBadge}>
                {t('header.navBar.userLinks.smbBundles.new')}
              </SuccessBadge>
              {t('header.searchBar.searchAttributes')}:{' '}
            </TextRegular>
            {suggestedAttributes?.map((attribute) => (
              <Pill
                className={Classes.childPill}
                translations={{
                  ariaLabelDefault: getDisplayLabel(attribute),
                  ariaLabelActive: getDisplayLabel(attribute),
                }}
                key={attribute.attributeValueId}
                onClick={() => redirectURL(attribute)}
              >
                {getDisplayLabel(attribute)}
              </Pill>
            ))}
          </Pills>
        </div>
        <div>
          <HighlightGuide
            targetRef={targetRef}
            show={showGuide}
            title={t('header.searchBar.suggestionGuideAttributeTitle')}
            description={t('header.searchBar.suggestionGuideAttributeContent')}
            onClose={handleCloseGuide}
            updateTrigger={triggerGuideUpdate}
            doneText={t('header.searchBar.done')}
          />
        </div>
      </>
    )
  );
};

export default AttributeSuggestionTooltip;
